import { NormalizedUnitNumber } from '@/domain/types/NumericValues'

import { CAP_WARNING_THRESHOLD, EulerSupplyValidationParams, EulerSupplyStatus, EulerValidationResult } from './types'

/**
 * Calculate cap utilization as a decimal (0-1)
 */
export function calculateCapUtilization(
  current: NormalizedUnitNumber,
  cap: NormalizedUnitNumber | undefined,
): number | undefined {
  if (!cap || cap.isZero()) return undefined
  return current.dividedBy(cap).toNumber()
}

/**
 * Calculate remaining capacity until cap
 */
export function calculateRemainingCapacity(
  current: NormalizedUnitNumber,
  cap: NormalizedUnitNumber | undefined,
): NormalizedUnitNumber | undefined {
  if (!cap) return undefined
  const remaining = cap.minus(current)
  return NormalizedUnitNumber(remaining.isNegative() ? '0' : remaining.toString())
}

/**
 * Validate supply action for Euler vaults
 */
export function validateEulerSupply(params: EulerSupplyValidationParams): EulerValidationResult {
  const { value, totalAssets, supplyCap, isPaused } = params

  // Check if vault is paused
  if (isPaused) {
    return {
      status: 'paused',
      message: 'Supply is currently paused for this vault',
      severity: 'error',
      canProceed: false,
    }
  }

  // No cap means unlimited supply
  if (!supplyCap || supplyCap.isZero()) {
    return {
      status: 'available',
      severity: 'info',
      canProceed: true,
    }
  }

  const utilization = calculateCapUtilization(totalAssets, supplyCap)
  const remaining = calculateRemainingCapacity(totalAssets, supplyCap)

  // Check if cap is reached
  if (utilization !== undefined && utilization >= 1) {
    return {
      status: 'cap-reached',
      message: 'Supply cap has been reached. No additional deposits are allowed.',
      severity: 'error',
      canProceed: false,
    }
  }

  // Check if value would exceed cap
  if (value && remaining && value.gt(remaining)) {
    return {
      status: 'cap-reached',
      message: `Your deposit would exceed the supply cap. Maximum deposit: ${remaining.toFixed(2)}`,
      severity: 'error',
      canProceed: false,
    }
  }

  // Check if approaching cap (warning threshold)
  if (utilization !== undefined && utilization >= CAP_WARNING_THRESHOLD) {
    const remainingStr = remaining ? remaining.toFixed(2) : 'N/A'
    return {
      status: 'cap-warning',
      message: `Supply cap is almost reached (${(utilization * 100).toFixed(1)}% utilized). Remaining capacity: ${remainingStr}`,
      severity: 'warning',
      canProceed: true,
    }
  }

  return {
    status: 'available',
    severity: 'info',
    canProceed: true,
  }
}

/**
 * Validate a deposit against the ERC4626 maxDeposit() remaining capacity.
 * This is the canonical way to check supply caps for Euler vaults.
 *
 * @param depositAmount - The amount the user wants to deposit (string from input)
 * @param maxDeposit    - maxDeposit() result converted to NormalizedUnitNumber
 * @param symbol        - Asset symbol for error messages
 */
export function validateMaxDeposit({
  depositAmount,
  maxDeposit,
  symbol,
}: {
  depositAmount?: string
  maxDeposit?: NormalizedUnitNumber
  symbol?: string
}): EulerValidationResult {
  if (maxDeposit === undefined) {
    return { status: 'available', severity: 'info', canProceed: true }
  }

  const depositValue = depositAmount && Number(depositAmount) > 0 ? NormalizedUnitNumber(depositAmount) : undefined

  if (maxDeposit.isZero()) {
    // Only block if the user is actually trying to deposit.
    // When no deposit is entered (e.g. pure borrow on existing position), show a
    // warning but allow proceeding — the supply cap doesn't affect non-deposit ops.
    if (depositValue) {
      return {
        status: 'cap-reached',
        message: 'Supply cap has been reached. No additional deposits are allowed.',
        severity: 'error',
        canProceed: false,
      }
    }
    return {
      status: 'cap-reached',
      message: 'Supply cap has been reached. No additional deposits are allowed.',
      severity: 'warning',
      canProceed: true,
    }
  }

  if (depositValue?.gt(maxDeposit)) {
    const sym = symbol || ''
    return {
      status: 'cap-reached',
      message: `Your deposit would exceed the supply cap. Maximum deposit: ${maxDeposit.toFixed(2)} ${sym}`.trim(),
      severity: 'error',
      canProceed: false,
    }
  }

  return { status: 'available', severity: 'info', canProceed: true }
}

/**
 * Get supply status without validating a specific value
 */
export function getEulerSupplyStatus(
  totalAssets: NormalizedUnitNumber,
  supplyCap: NormalizedUnitNumber | undefined,
  isPaused?: boolean,
): EulerSupplyStatus {
  if (isPaused) return 'paused'
  if (!supplyCap || supplyCap.isZero()) return 'available'

  const utilization = calculateCapUtilization(totalAssets, supplyCap)
  if (utilization === undefined) return 'available'
  if (utilization >= 1) return 'cap-reached'
  if (utilization >= CAP_WARNING_THRESHOLD) return 'cap-warning'

  return 'available'
}
